import { Component, OnInit } from "@angular/core";
import { TuiInputModule, TuiInputPasswordModule } from "@taiga-ui/legacy";
import { ActivatedRoute, Router } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiError,
} from "@taiga-ui/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ForgotResetPasswordService } from "../../core/services/forgot-reset-password.service.service";
import { AsyncPipe, CommonModule } from "@angular/common";
import { TuiFieldErrorPipe } from "@taiga-ui/kit";
import { EnterSubmitDirective } from "../../core/directives/enter-submit.directive";

@Component({
  selector: "app-reset-password",
  imports: [
    EnterSubmitDirective,
    TuiInputModule,
    TuiButton,
    ReactiveFormsModule,
    CommonModule,
    TuiAppearance,
    TuiInputPasswordModule,
  ],
  templateUrl: "./reset-password.component.html",
  styleUrl: "./reset-password.component.scss",
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm: FormGroup;
  isLoading: boolean = false;
  authError: string = "";
  userToken: string | null = null;
  userType: string = "";
  validating = true;

  constructor(
    private fb: FormBuilder,
    private forgotResetPasswordService: ForgotResetPasswordService,
    private route: ActivatedRoute,
    private router: Router,
    private alerts: TuiAlertService
  ) {
    this.resetPasswordForm = this.fb.group({
      newPassword: [
        "",
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
          ),
        ],
      ],
      confirmPassword: [
        "",
        [Validators.required, this.passwordMatchValidator.bind(this)],
      ],
    });
  }

  ngOnInit(): void {
    this.userToken = this.route.snapshot.paramMap.get("token");
    this.userType = this.route.snapshot.paramMap.get("userType") || "";
    this.validateToken();
  }

  passwordMatchValidator(
    control: FormControl
  ): { [s: string]: boolean } | null {
    if (!control.value) {
      return { required: true };
    } else if (
      control.value !== this.resetPasswordForm.controls["newPassword"].value
    ) {
      return { mismatch: true };
    }
    return null;
  }

  getPasswordError() {
    const control = this.resetPasswordForm.get("newPassword");
    if (control?.hasError("required")) return "Password is required.";
    if (control?.hasError("pattern"))
      return "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character";
    if (control?.hasError("minLength"))
      return "Password must contain at least 8 characters";
    return "";
  }

  getConfirmPasswordError() {
    const control = this.resetPasswordForm.get("confirmPassword");
    if (control?.hasError("required")) return "Confirm Password is required.";
    if (control?.hasError("mismatch")) return "Passwords do not match.";
    return "";
  }

  async validateToken(): Promise<void> {
    try {
      if (this.userType === "admin") {
        this.forgotResetPasswordService
          .validatePWResetToken(this.userToken)
          .subscribe(
            (response: any) => {
              if (!response.done) {
                this.alerts
                  .open("error", {
                    label: "Token is invalid or expired!",
                    appearance: "negative",
                  })
                  .subscribe();
                this.validating = false;
              }
            },
            (error) => {
              this.alerts
                .open("error", {
                  label: "Token validation failed!",
                  appearance: "negative",
                })
                .subscribe();
              this.validating = false;
            }
          );
      }
      if (this.userType === "nurse") {
        this.forgotResetPasswordService
          .validatePWResetTokenNurse(this.userToken)
          .subscribe(
            (response: any) => {
              if (!response.done) {
                this.alerts
                  .open("error", {
                    label: "Token is invalid or expired!",
                    appearance: "negative",
                  })
                  .subscribe();
                this.validating = false;
              }
            },
            (error) => {
              this.alerts
                .open("error", {
                  label: "Token validation failed!",
                  appearance: "negative",
                })
                .subscribe();
              this.validating = false;
            }
          );
      }
      if (this.userType === "client") {
        this.forgotResetPasswordService
          .validatePWResetTokenClient(this.userToken)
          .subscribe(
            (response: any) => {
              if (!response.done) {
                this.alerts
                  .open("error", {
                    label: "Token is invalid or expired!",
                    appearance: "negative",
                  })
                  .subscribe();
                this.validating = false;
              }
            },
            (error) => {
              this.alerts
                .open("error", {
                  label: "Token validation failed!",
                  appearance: "negative",
                })
                .subscribe();
              this.validating = false;
            }
          );
      }
    } catch (error) {
      this.alerts
        .open("error", {
          label: "Token validation failed!",
          appearance: "negative",
        })
        .subscribe();
      this.validating = false;
    }
  }

  onSubmit() {
    if (this.resetPasswordForm.invalid) {
      Object.keys(this.resetPasswordForm.controls).forEach((field) => {
        const control = this.resetPasswordForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      return;
    }

    this.isLoading = true;
    const formData = this.resetPasswordForm.getRawValue();
    const payload = {
      token: this.userToken,
      password: formData.newPassword,
    };

    if (this.userType === "admin") {
      this.forgotResetPasswordService.resetPassword(payload).subscribe(
        (res: any) => {
          if (res.done) {
            this.alerts
              .open("Password has been reset successfully.", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.router.navigate(["/welcome"]);
          } else {
            this.alerts
              .open("error", {
                label: res.message || "Password reset failed. Please try again.",
                appearance: "negative",
              })
              .subscribe();
          }
          this.isLoading = false;
        },
        (error) => {
          this.alerts
            .open("error", {
              label: "Password reset failed. Please try again.",
              appearance: "negative",
            })
            .subscribe();
          this.isLoading = false;
        }
      );
    }

    if (this.userType === "nurse") {
      this.forgotResetPasswordService.resetPasswordNurse(payload).subscribe(
        (res: any) => {
          if (res.done) {
            this.alerts
              .open("Password has been reset successfully.", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.router.navigate(["/welcome"]);
          } else {
            this.alerts
              .open("error", {
                label: res.message || "Password reset failed. Please try again.",
                appearance: "negative",
              })
              .subscribe();
          }
          this.isLoading = false;
        },
        (error) => {
          this.alerts
            .open("error", {
              label: "Password reset failed. Please try again.",
              appearance: "negative",
            })
            .subscribe();
          this.isLoading = false;
        }
      );
    }

    if (this.userType === "client") {
      this.forgotResetPasswordService.resetPasswordClient(payload).subscribe(
        (res: any) => {
          if (res.done) {
            this.alerts
              .open("Password has been reset successfully.", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.router.navigate(["/welcome"]);
          } else {
            this.alerts
              .open("error", {
                label: res.message || "Password reset failed. Please try again.",
                appearance: "negative",
              })
              .subscribe();
          }
          this.isLoading = false;
        },
        (error) => {
          this.alerts
            .open("error", {
              label: "Password reset failed. Please try again.",
              appearance: "negative",
            })
            .subscribe();
          this.isLoading = false;
        }
      );
    }
  }
}
